import { CardSuggestionRoute, Dialog, SidebarCheckpoint, SidebarDetail, TableOnGoingTrip } from "@components";
import { ApplicationLayout, Divider, Sidebar } from "@rio-cloud/rio-uikit";
import { ROUTE_COLOR_SUCCESS } from "@rio-cloud/rio-uikit/Route";
import { CheckpointStep } from "@types";
import React, { useMemo, useState } from "react";
import CardCount from "src/components/CardCount";
import RioMap from "src/components/Map";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";
import { useOnGoingTripsController } from "./components/useOnGoingTripsController";
import UrgentAlertBannerList from "src/components/UrgentAlertBanner";

const OnGoingTripsScreen = () => {
  const [stage, setStage] = useState<CheckpointStep>();
  const [openDialog, setOpenDialog] = useState(false);

  const {
    routes,
    followers,
    searchValue,
    selected,
    details,
    page,
    pages,
    isPolyline,
    dashboard,
    filterByType,
    position,
    boundingBox,
    segments,
    markers,
    view,
    closed,
    stops,
    currencySegments,
    suggestedSegments,
    suggestedRouteData,
    statusTripNow,
    statusRouteNow,
    progressNow,
    markerNow,
    locationNow,
    checkpoint,
    openStages,
    handleSelectFilterByType,
    handleDetails,
    handleShare,
    handleSort,
    handlePage,
    handleSearchValueChange,
    handleCloseSidebar,
    loadCheckpointDetails,
    setOpenStages,
    setClosed,
    handleAcceptTrip,
    handleFinishTrip,
    PAGE_SIZE,
    suggestedRoutes,
    rioDrivers,
    isLoading,
    isLoadingMore,
    zoom,
    totalTrips,
    size,
    handleResetPage,
    isLoadingAcceptTrip,
    urgentAlerts,
    urgentAlertMarkers,
    loadUrgentAlerts,
  } = useOnGoingTripsController();

  const allMarkers = useMemo(() => {
    const base = markerNow?.position?.lat ? [...markers, markerNow] : markers;
    return [...base, ...urgentAlertMarkers];
  }, [markers, markerNow, urgentAlertMarkers]);

  const handleAlertDisarmed = (alertId: string) => {
    if (selected?.tripId) {
      loadUrgentAlerts(selected.tripId);
    }
  };

  return (
    <React.Fragment>
      {view === "DEFAULT" ? (
        <MapDrawingProvider>
          <div className="padding-20 display-flex flex-column gap-20">
            <div className="display-flex gap-20">
              <CardCount
                isActive={filterByType === "STARTED"}
                onClick={() => handleSelectFilterByType("STARTED")}
                borderColor="#30B4C0"
                label="VIAGENS EM ANDAMENTO"
                value={dashboard.ongoingRoute}
              />

              <CardCount
                isActive={filterByType === "OUTROUTE"}
                onClick={() => handleSelectFilterByType("OUTROUTE")}
                borderColor="#D90000"
                label="FORA DE ROTA"
                value={dashboard.outOfRoute}
              />

              <CardCount
                isActive={filterByType === "OVERDUE"}
                onClick={() => handleSelectFilterByType("OVERDUE")}
                borderColor="#D90000"
                label="ATRASADAS"
                value={dashboard.overdueRoute}
              />

              <CardCount
                isActive={filterByType === "EARLY"}
                onClick={() => handleSelectFilterByType("EARLY")}
                borderColor="#FF8E3C"
                label="ADIANTADAS"
                value={dashboard.earlyRoute}
              />
            </div>

            <TableOnGoingTrip
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              pageSize={PAGE_SIZE}
              size={size || PAGE_SIZE}
              totalSize={totalTrips || 0}
              handlePage={handlePage}
              handleResetPage={handleResetPage}
              handleDetails={handleDetails}
              selected={selected?.routeId}
              handleSelect={() => {}}
              followers={followers}
              pages={pages}
              totalTrips={totalTrips}
              handleSort={handleSort}
              routes={routes}
              suggestedRoutes={suggestedRoutes}
              viewType="TABLE"
              page={page}
              handleSearchValueChange={handleSearchValueChange}
              searchValue={searchValue}
              rioDrivers={rioDrivers}
              handleAcceptTrip={handleAcceptTrip}
            />
          </div>
        </MapDrawingProvider>
      ) : (
        <>
          <ApplicationLayout.Sidebar className="right z-index-2 height-100pct">
            <Sidebar
              title="Detalhes da viagem"
              closed={closed}
              onClose={handleCloseSidebar}
              position={Sidebar.RIGHT}
              width={500}
            >
              <div className="padding-left-20 padding-top-20 padding-right-20 padding-bottom-20">
                {suggestedSegments.length > 0 && (
                  <CardSuggestionRoute
                    strategy={suggestedRouteData?.strategy}
                    saving={selected?.suggest}
                    isLoading={isLoadingAcceptTrip}
                    onAccept={async () => {
                      await handleAcceptTrip(selected?.tripId || "", true);
                    }}
                    onRefuse={async () => {
                      await handleAcceptTrip(selected?.tripId || "", false);
                    }}
                  />
                )}
                <SidebarDetail
                  currencyLocation={locationNow}
                  isPolyline={isPolyline}
                  handleShare={handleShare}
                  detail={selected}
                  origin={details?.originRoute}
                  destiny={details?.destinyRoute}
                  statusTripNow={statusTripNow}
                  statusRouteNow={statusRouteNow}
                  progressNow={progressNow}
                  stops={stops}
                  followers={followers}
                  onClickCheckpoint={(checkpointId: string) => loadCheckpointDetails(checkpointId)}
                  onFinishTrip={() => selected?.tripId && handleFinishTrip(selected.tripId)}
                />
              </div>
              {urgentAlerts.length > 0 && (
                <>
                  <Divider />
                  <UrgentAlertBannerList
                    alerts={urgentAlerts}
                    onDisarmed={handleAlertDisarmed}
                  />
                </>
              )}
            </Sidebar>

            <SidebarCheckpoint
              open={!openStages}
              data={checkpoint}
              onClose={() => {
                setOpenStages(false);
                setClosed(false);
              }}
              onClickItemInfo={(stage) => {
                setStage(stage);
                setOpenDialog(true);
              }}
            />
            <Dialog.Checkpoint
              totalStages={checkpoint?.steps?.length || 0}
              open={openDialog}
              stage={stage}
              onClose={() => {
                setOpenDialog(false);
              }}
              tripId={selected?.tripId}
              checkpointId={checkpoint?.id}
              checkpointStop={checkpoint}
            />
          </ApplicationLayout.Sidebar>

          <div className="width-100pct height-100pct">
            <MapDrawingProvider>
              <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
                <RioMap
                  position={position}
                  boundingBox={boundingBox}
                  zoom={zoom}
                  apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
                  showAreas={false}
                  segments={[
                    ...(segments.length ? [{ points: segments, alternative: false }] : []),
                    ...(suggestedSegments.length
                      ? [{ points: suggestedSegments, suggestedColor: ROUTE_COLOR_SUCCESS }]
                      : []),
                  ]}
                  currencySegments={[
                    ...(currencySegments.length ? [{ points: currencySegments, alternative: false }] : []),
                  ]}
                  markers={allMarkers}
                  useClusters={false}
                />
              </ApplicationLayout.Body>
            </MapDrawingProvider>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export default OnGoingTripsScreen;
